// File: frontend/src/pages/Activities/ActivitiesPage.jsx
// Activities timeline with comprehensive filters and management

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { ExportActivitiesButton } from '../../components/Export/ExportButton';
import { ActivityForm } from '../../components/Activities';
import {
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  DevicePhoneMobileIcon,
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const ActivitiesPage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // State for filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [selectedContact, setSelectedContact] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  
  // Modal states
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);

  // Debounced search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch activities data
  const { data: activitiesData, isLoading, error } = useQuery(
    ['activities', { 
      search: debouncedSearchTerm, 
      page: currentPage, 
      pageSize, 
      type: selectedType,
      status: selectedStatus,
      priority: selectedPriority,
      contact: selectedContact,
      company: selectedCompany,
      dateFrom,
      dateTo
    }],
    async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });
      
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (selectedType) params.append('type', selectedType);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedPriority) params.append('priority', selectedPriority);
      if (selectedContact) params.append('contact_id', selectedContact);
      if (selectedCompany) params.append('company_id', selectedCompany);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await api.get(`/activities?${params}`);
      return response.data;
    },
    {
      enabled: !!user?.id,
      keepPreviousData: true,
    }
  );

  // Fetch contacts for filter
  const { data: contacts } = useQuery(
    'contacts-list',
    async () => {
      const response = await api.get('/contacts?limit=100');
      return response.data.data || [];
    },
    { enabled: !!user?.id }
  );

  // Fetch companies for filter
  const { data: companies } = useQuery(
    'companies-list',
    async () => {
      const response = await api.get('/companies?limit=100');
      return response.data.data || [];
    },
    { enabled: !!user?.id }
  );

  // Delete activity mutation
  const deleteMutation = useMutation(
    (activityId) => api.delete(`/activities/${activityId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('activities');
        toast.success('Atividade excluída com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao excluir atividade');
      },
    }
  );

  const activities = activitiesData?.data || [];
  const totalActivities = activitiesData?.pagination?.total_items || 0;
  const totalPages = Math.ceil(totalActivities / pageSize);

  // Activity types and their configurations
  const activityTypes = [
    { value: 'call', label: 'Ligação', icon: PhoneIcon },
    { value: 'email', label: 'Email', icon: EnvelopeIcon },
    { value: 'meeting', label: 'Reunião', icon: CalendarIcon },
    { value: 'note', label: 'Nota', icon: ChatBubbleLeftRightIcon },
    { value: 'task', label: 'Tarefa', icon: ClipboardDocumentListIcon },
    { value: 'whatsapp', label: 'WhatsApp', icon: DevicePhoneMobileIcon },
    { value: 'linkedin', label: 'LinkedIn', icon: UserGroupIcon }
  ];

  // Get activity icon component
  const getActivityIcon = (type) => {
    const activityType = activityTypes.find(t => t.value === type);
    return activityType ? activityType.icon : ClipboardDocumentListIcon;
  };

  // Get activity type label
  const getActivityTypeLabel = (type) => {
    const activityType = activityTypes.find(t => t.value === type);
    return activityType ? activityType.label : type;
  };

  // Get priority colors
  const getPriorityColor = (priority) => {
    const colorMap = {
      'high': 'bg-red-100 text-red-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-gray-100 text-gray-800'
    };
    return colorMap[priority] || colorMap.medium;
  };

  // Get status colors
  const getStatusColor = (status) => {
    const colorMap = {
      'completed': 'bg-green-100 text-green-800',
      'pending': 'bg-blue-100 text-blue-800'
    };
    return colorMap[status] || colorMap.pending;
  };

  // Format priority label
  const formatPriority = (priority) => {
    const formatMap = {
      'high': 'Alta',
      'medium': 'Média',
      'low': 'Baixa'
    };
    return formatMap[priority] || priority;
  };

  // Format status label
  const formatStatus = (status) => {
    const formatMap = {
      'completed': 'Concluída',
      'pending': 'Pendente'
    };
    return formatMap[status] || status;
  };

  const handleDeleteActivity = (activity) => {
    if (window.confirm(`Tem certeza que deseja excluir a atividade "${activity.subject}"?`)) {
      deleteMutation.mutate(activity.id);
    }
  };

  const handleEditActivity = (activity) => {
    setEditingActivity(activity);
    setShowActivityForm(true);
  };

  const handleCloseActivityForm = () => {
    setShowActivityForm(false);
    setEditingActivity(null);
  };

  const handleActivityFormSuccess = () => {
    queryClient.invalidateQueries('activities');
    handleCloseActivityForm();
  };

  // Calculate stats
  const todayActivities = activities?.filter(activity => {
    const activityDate = new Date(activity.activity_date || activity.due_date).toDateString();
    const today = new Date().toDateString();
    return activityDate === today;
  }).length || 0;

  const overdueActivities = activities?.filter(activity => {
    const activityDate = new Date(activity.activity_date || activity.due_date);
    const today = new Date();
    return activityDate < today && !activity.completed;
  }).length || 0;

  const completedThisWeek = activities?.filter(activity => {
    const activityDate = new Date(activity.completed_at || activity.updated_at);
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return activityDate > weekAgo && activity.completed;
  }).length || 0;

  // Group activities by date
  const groupActivitiesByDate = (activities) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - 7);

    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: []
    };

    activities.forEach(activity => {
      const activityDate = new Date(activity.activity_date || activity.due_date);
      const activityDateString = activityDate.toDateString();

      if (activityDateString === today.toDateString()) {
        groups.today.push(activity);
      } else if (activityDateString === yesterday.toDateString()) {
        groups.yesterday.push(activity);
      } else if (activityDate > thisWeek) {
        groups.thisWeek.push(activity);
      } else {
        groups.older.push(activity);
      }
    });

    return groups;
  };

  const groupedActivities = groupActivitiesByDate(activities);

  const renderTimelineGroup = (title, activities, showDate = false) => {
    if (activities.length === 0) return null;

    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 sticky top-0 bg-gray-50 dark:bg-gray-950 py-2 border-b">
          {title} ({activities.length})
        </h3>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
          
          <div className="space-y-6">
            {activities.map((activity, index) => {
              const IconComponent = getActivityIcon(activity.type);
              
              return (
                <div key={activity.id} className="relative flex items-start space-x-4">
                  {/* Timeline dot */}
                  <div className="flex-shrink-0 relative">
                    <div className="w-12 h-12 bg-white dark:bg-gray-800 border-2 border-blue-200 rounded-full flex items-center justify-center shadow-sm">
                      <IconComponent className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  
                  {/* Activity content */}
                  <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {activity.subject}
                          </h4>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(activity.priority)}`}>
                            {formatPriority(activity.priority)}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                            {formatStatus(activity.status)}
                          </span>
                        </div>
                        
                        {activity.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                            {activity.description}
                          </p>
                        )}
                        
                        <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          <div className="flex items-center space-x-1">
                            <ClockIcon className="h-4 w-4" />
                            <span>
                              {new Date(activity.activity_date || activity.due_date).toLocaleDateString('pt-BR')}
                              {activity.due_time && ` às ${activity.due_time}`}
                            </span>
                          </div>
                          
                          {activity.contact && (
                            <Link 
                              to={`/contacts/${activity.contact.id}`}
                              className="font-medium text-blue-600 hover:text-blue-800"
                            >
                              {activity.contact.first_name} {activity.contact.last_name}
                            </Link>
                          )}
                          
                          {activity.company && (
                            <Link 
                              to={`/companies/${activity.company.id}`}
                              className="font-medium text-green-600 hover:text-green-800"
                            >
                              {activity.company.name}
                            </Link>
                          )}
                          
                          {activity.user && (
                            <span>por {activity.user.first_name} {activity.user.last_name}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                          title="Ver detalhes"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditActivity(activity)}
                          className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 rounded-lg"
                          title="Editar atividade"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteActivity(activity)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                          title="Excluir atividade"
                          disabled={deleteMutation.isLoading}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Gestão de Atividades
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Timeline e histórico de todas as interações
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <ExportActivitiesButton
              variant="secondary"
              size="md"
              filters={{
                type: selectedType || undefined,
                start_date: dateFrom || undefined,
                end_date: dateTo || undefined
              }}
            />
            <button
              onClick={() => setShowActivityForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Nova Atividade</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por assunto ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-950"
          >
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span>Filtros</span>
            {showFilters && <XMarkIcon className="h-4 w-4" />}
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos os tipos</option>
                  {activityTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos os status</option>
                  <option value="pending">Pendente</option>
                  <option value="completed">Concluída</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prioridade
                </label>
                <select
                  value={selectedPriority}
                  onChange={(e) => setSelectedPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todas as prioridades</option>
                  <option value="high">Alta</option>
                  <option value="medium">Média</option>
                  <option value="low">Baixa</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contato
                </label>
                <select
                  value={selectedContact}
                  onChange={(e) => setSelectedContact(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos os contatos</option>
                  {contacts?.map(contact => (
                    <option key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa
                </label>
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todas as empresas</option>
                  {companies?.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data até
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total de Atividades</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{totalActivities}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <CalendarIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Atividades de Hoje</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{todayActivities}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Em Atraso</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{overdueActivities}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <CheckIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Concluídas (7 dias)</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{completedThisWeek}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-gray-50 dark:bg-gray-950 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        {isLoading ? (
          <div className="space-y-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
                <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg p-4">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="text-red-600 mb-4">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Erro ao carregar atividades
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              {error.response?.data?.message || 'Tente novamente mais tarde'}
            </p>
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8">
            <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Nenhuma atividade encontrada
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Tente ajustar os filtros ou criar uma nova atividade.
            </p>
          </div>
        ) : (
          <div>
            {renderTimelineGroup("Hoje", groupedActivities.today)}
            {renderTimelineGroup("Ontem", groupedActivities.yesterday)}
            {renderTimelineGroup("Esta Semana", groupedActivities.thisWeek)}
            {renderTimelineGroup("Anteriores", groupedActivities.older)}
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Mostrando {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalActivities)} de {totalActivities} atividades
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-950"
                >
                  Anterior
                </button>
                <span className="px-3 py-1 text-sm">
                  {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-950"
                >
                  Próximo
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Activity Form Modal */}
      <ActivityForm
        isOpen={showActivityForm}
        onClose={handleCloseActivityForm}
        activity={editingActivity}
        onSuccess={handleActivityFormSuccess}
      />
    </div>
  );
};

export default ActivitiesPage;